import { ComponentChildren } from "preact";
import { useState } from "preact/hooks";
import styles from "./Checkbox.module.scss";

export interface CheckboxProps {
  checked?: boolean;
  defaultChecked?: boolean;
  disabled?: boolean;
  onChange?: (_checked: boolean) => void;
  children?: ComponentChildren;
  className?: string;
  id?: string;
  name?: string;
  value?: string;
  size?: "sm" | "md" | "lg";
  variant?: "default" | "border";
}

export function Checkbox({
  checked,
  defaultChecked = false,
  disabled = false,
  onChange,
  children,
  className = "",
  id,
  name,
  value,
  size: _size = "md",
  variant = "default",
}: CheckboxProps) {
  const [internalChecked, setInternalChecked] = useState(defaultChecked);

  const isControlled = checked !== undefined;
  const isChecked = isControlled ? checked : internalChecked;

  const handleChange = () => {
    if (disabled) return;

    const newChecked = !isChecked;

    if (!isControlled) {
      setInternalChecked(newChecked);
    }

    onChange?.(newChecked);
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === " " || event.key === "Enter") {
      event.preventDefault();
      handleChange();
    }
  };

  const handlePress = (event: MouseEvent | TouchEvent) => {
    event.preventDefault();
    handleChange();
  };

  const checkboxClasses = [
    styles.checkbox,
    isChecked ? styles.checked : styles.unchecked,
    variant === "border" ? styles.border : "",
    disabled ? styles.disabled : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const wrapperClasses = [
    styles.wrapper,
    disabled ? styles.wrapperDisabled : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <label
      className={wrapperClasses}
      onMouseDown={handlePress}
      onTouchStart={handlePress}
    >
      <input
        type="checkbox"
        checked={isChecked}
        disabled={disabled}
        onChange={() => {}}
        onKeyDown={handleKeyDown}
        className={styles.hiddenInput}
        id={id}
        name={name}
        value={value}
      />

      <div className={checkboxClasses}>
        <div className={styles.track}>
          <div className={styles.thumb} />
        </div>
      </div>

      {children && <span className={styles.label}>{children}</span>}
    </label>
  );
}
